/**
 * useSourceSelection — единое место состояния выбора источника(ов) для чата.
 *
 * Зачем выделено:
 * - SRP: composable отвечает только за выбор источников и их per-thread конфиг.
 *   Сам `useChatStore` ничего не знает про разный UI для разных kinds —
 *   он просто читает `selected` и `allowedForRequest()`.
 * - OCP: добавление нового типа источника (Files / RAG / database / ...)
 *   = новая запись в `SOURCE_META` + опционально компонент-настройка.
 *   Никакого if-else по `kind` в основной логике.
 * - LSP: все источники одинаково сериализуются в payload
 *   (`{ code, config }`), независимо от kind.
 *
 * Контракт ответа `GET /api/chat/sources/available` — массив объектов вида:
 *   { id (=code), label (=name), kind, description }
 */

import { computed, reactive, ref } from 'vue'

/**
 * Метаданные UI для каждого `kind`.
 *
 * Поля:
 * - icon            — иконка в селекторе и в чипе.
 * - label(source)   — функция, возвращающая отображаемое имя.
 * - hasConfig       — есть ли у источника настройки (открывают модалку/диалог).
 * - configComponent — имя глобального компонента-настройки (для <component :is>).
 *                     Сейчас файлы FilesSourceConfig.vue и RagSourceConfig.vue
 *                     — заглушки, чтобы можно было проверить полиморфизм mounting'а.
 */
export const SOURCE_META = {
    internal: {
        icon: 'database',
        label: (source) => source?.label || 'Внутренние данные',
        hasConfig: false,
        configComponent: null,
    },
    mcp: {
        icon: 'globe',
        label: (source) => source?.label || source?.id || 'Внешний источник',
        hasConfig: false,
        configComponent: null,
    },
    files: {
        icon: 'paperclip',
        label: () => 'Файлы',
        hasConfig: true,
        configComponent: 'FilesSourceConfig',
    },
    preloaded_files: {
        icon: 'paperclip',
        label: (source) => source?.label || 'Файлы (предзагруженные)',
        hasConfig: true,
        configComponent: 'PreloadedFilesSourceConfig',
    },
    rag: {
        icon: 'database-search',
        label: (source) => source?.label || 'База знаний',
        hasConfig: true,
        configComponent: 'RagSourceConfig',
    },
}

/** Универсальный фоллбек, если backend прислал неизвестный kind. */
const FALLBACK_META = SOURCE_META.internal

export function metaForSource(source) {
    if (!source) return FALLBACK_META
    return SOURCE_META[source.kind] || FALLBACK_META
}

export function iconForSource(source) {
    return metaForSource(source).icon
}

export function labelForSource(source) {
    return metaForSource(source).label(source)
}

/**
 * Composable: per-store состояние выбранного источника.
 *
 * Возвращает реактивные ref/computed:
 * - `available`        — список доступных пользователю источников (с сервера).
 * - `selected`         — `{ code, config }` (config — произвольный объект).
 * - `selectedMeta`     — UI-мета выбранного.
 * - `selectSource(code)`     — переключить.
 * - `updateConfig(patch)`    — слить patch в config выбранного.
 * - `allowedForRequest()`    — массив `{ code, config }` для отправки на сервер.
 * - `setAvailable(list)`     — обновить список из API-ответа.
 */
export function useSourceSelection(initialCode = 'clickhouse_excel') {
    const available = ref([])
    const selected = reactive({ code: initialCode, config: {} })

    const selectedSource = computed(
        () => available.value.find((s) => s.id === selected.code) || null
    )
    const selectedMeta = computed(() => metaForSource(selectedSource.value))

    function setAvailable(list) {
        available.value = Array.isArray(list) ? list : []
        // Если ранее выбранный источник больше не доступен — сбрасываем.
        if (selected.code && !available.value.some((s) => s.id === selected.code)) {
            const fallback = available.value[0]
            selected.code = fallback ? fallback.id : ''
            selected.config = {}
        }
    }

    function selectSource(code) {
        if (selected.code === code) return
        selected.code = code
        selected.config = {}
    }

    function updateConfig(patch) {
        selected.config = { ...selected.config, ...patch }
    }

    function allowedForRequest() {
        if (!selected.code) return []
        return [{ code: selected.code, config: selected.config || {} }]
    }

    return {
        available,
        selected,
        selectedSource,
        selectedMeta,
        setAvailable,
        selectSource,
        updateConfig,
        allowedForRequest,
    }
}
